import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { getRegiaoMetropolitana } from "@/lib/regioes-metropolitanas";

function getSupabasePublic() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    {
      auth: {
        storage: undefined,
        persistSession: false,
        autoRefreshToken: false,
      },
    },
  );
}

const PAINEL_COOKIE_NAME = "painel-auth";
const PAINEL_SESSION_TOKEN = "painel-session-2024-secure-token";

function isPainelAuthenticated(): boolean {
  const request = getRequest();
  if (!request) return false;
  const cookieHeader = request.headers.get("cookie") ?? "";
  const match = cookieHeader.match(
    new RegExp(`(?:^|;)\\s*${PAINEL_COOKIE_NAME}=([^;]*)`),
  );
  const token = match ? decodeURIComponent(match[1]) : undefined;
  return token === PAINEL_SESSION_TOKEN;
}

export const getLandingPagePreview = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string() }).parse(input))
  .handler(async ({ data }) => {
    if (!isPainelAuthenticated()) {
      throw new Error("UNAUTHORIZED");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("landing_pages")
      .select(
        "*, matrix:keyword_matrices(*)",
      )
      .eq("slug", data.slug)
      .limit(1);

    if (error) throw new Error(error.message);
    const row: any = rows?.[0];
    if (!row) return null;

    const matrix = row.matrix ?? null;
    if (matrix) {
      if (!row.texto_hero && matrix.template_hero) {
        row.texto_hero = matrix.template_hero;
      }
    }
    return row;
  });

export const getLandingPageBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string() }).parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("landing_pages")
      .select("*, matrix:keyword_matrices(*)")
      .eq("slug", data.slug)
      .eq("published", true)
      .limit(1);

    if (error) throw new Error(error.message);
    const row = rows?.[0];
    if (!row) return null;

    // Apply matrix templates as fallback for missing texts
    const matrix = (row as any).matrix ?? null;
    if (matrix) {
      if (!row.texto_hero && matrix.template_hero) {
        row.texto_hero = matrix.template_hero;
      }
    }
    return row;
  });

export const getAllPublishedLandingPages = createServerFn({ method: "GET" })
  .handler(async () => {
    const supabase = getSupabasePublic();
    const { data: rows, error } = await supabase
      .from("landing_pages")
      .select("slug, updated_at, title")
      .eq("published", true)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const getClusterPagesForMatrix = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        matrixId: z.string(),
        excludeSlug: z.string().nullable().optional(),
        currentCidade: z.string().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = getSupabasePublic();
    let query = supabase
      .from("landing_pages")
      .select(
        "slug, cidade, bairro, preposicao_cidade, preposicao_bairro, palavra_chave, title",
      )
      .eq("published", true)
      .eq("matrix_id", data.matrixId);
    if (data.excludeSlug) {
      query = query.neq("slug", data.excludeSlug);
    }
    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return filterOutOtherRegions(rows ?? [], data.currentCidade ?? null);
  });

export const getClusterPagesForSegmento = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        segmento: z.string(),
        currentCidade: z.string().nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Find matrix id(s) for the segmento
    const { data: matrices } = await supabaseAdmin
      .from("keyword_matrices")
      .select("id")
      .eq("segmento", data.segmento);
    const ids = (matrices ?? []).map((m) => m.id);
    if (ids.length === 0) return [];
    const supabase = getSupabasePublic();
    const { data: rows, error } = await supabase
      .from("landing_pages")
      .select(
        "slug, cidade, bairro, preposicao_cidade, preposicao_bairro, palavra_chave, title",
      )
      .eq("published", true)
      .in("matrix_id", ids);
    if (error) throw new Error(error.message);
    return filterOutOtherRegions(rows ?? [], data.currentCidade ?? null);
  });

/**
 * Só remove páginas cuja cidade pertence a uma região metropolitana
 * conhecida DIFERENTE da cidade-base atual. Por padrão mantém tudo — o
 * interior de SP e cidades não mapeadas continuam aparecendo. Assim,
 * quando existir landing do Rio no futuro, só o cluster do Rio puxa Grande
 * Rio, e as landings de SP não mostram nada do Rio.
 */
function filterOutOtherRegions(
  rows: Array<{ cidade: string | null; bairro: string | null }>,
  currentCidade: string | null,
): typeof rows {
  const currentRegion =
    getRegiaoMetropolitana(currentCidade) ??
    (currentCidade && currentCidade.trim() ? currentCidade : "São Paulo");
  return rows.filter((r) => {
    const rowRegion = getRegiaoMetropolitana(r.cidade);
    // Só filtra fora se a linha pertence explicitamente a OUTRA região conhecida.
    if (rowRegion && rowRegion !== currentRegion) return false;
    return true;
  });
}
